import type { LegalDocPair } from '../types'

/**
 * Términos de dominios.
 *
 * véltica revende dominios a través de un registrador socio: el dominio se
 * registra a nombre del cliente, se cobra aparte de la membresía, se renueva
 * cada año en su aniversario con aviso previo, y el primer año puede ir
 * incluido con un plan anual (un crédito, no una promesa de dominio gratis para
 * siempre). Reembolsar o disputar la membresía que dio ese crédito bloquea la
 * transferencia del dominio.
 */
export const DOMAINS: LegalDocPair = {
  en: {
    title: 'Domain Terms',
    summary: 'The rules for registering, renewing, transferring and connecting domains.',
    intro: [
      'These Domain Terms are part of the [Terms of Service](doc:terms). They apply if you register a domain through véltica or connect a domain to a Site.',
    ],
    sections: [
      {
        title: 'Domains you register through véltica',
        subs: [
          {
            title: 'Our role',
            blocks: [
              'We resell domain registration through a registrar partner. The registry and the registrar, not véltica, operate the domain system. Registration is subject to their rules and to the policies of ICANN, including the Uniform Domain-Name Dispute-Resolution Policy. We cannot guarantee that a name you search for will be available until the registration is completed.',
            ],
          },
          {
            title: 'You are the registrant',
            blocks: [
              'The domain is registered in your name and you are the registrant. You must give accurate, current contact information and keep it up to date, and you must respond to verification emails from the registrar or registry. If you do not, the domain can be suspended. Registrant contact data is shared with the registrar and registry and may appear in public registration directories, unless it is redacted or privacy-protected under their policies.',
            ],
          },
          {
            title: 'Price and payment',
            blocks: [
              'Domain prices depend on the extension and include the cost charged to us plus a service fee. The price for registration and the price for renewal are shown before you buy, and they can differ. A domain is charged separately from your plan, unless your plan includes a credit as described in section 2.',
            ],
          },
          {
            title: 'Renewal and expiry',
            blocks: [
              'Each domain renews every year on the anniversary of its registration, charging the payment method you saved. We tell you before each renewal, and you can turn automatic renewal off for a domain. If a renewal cannot be charged, the domain may expire, and it may enter a redemption period with extra fees or be released for anyone to register. We are not responsible for a domain you let expire.',
            ],
          },
          {
            title: 'No refunds once registered or renewed',
            blocks: [
              'Once a domain is registered or renewed, the charge is not refundable, because the registry does not refund it to us, except where the law requires it or the registration failed because of a fault of ours.',
            ],
          },
          {
            title: 'Transfers',
            blocks: [
              'The domain is yours and you can transfer it to another registrar. You can request the transfer authorization code from your dashboard. Transfers are subject to the rules of the registry, including a lock of 60 days after a new registration or a change of registrant, and to the locks described in these terms.',
            ],
          },
        ],
      },
      {
        title: 'Domain included with an annual plan',
        blocks: [
          'Some annual plans include a credit toward the first year of registration of one domain. The credit applies to the registration only, not to renewals or other charges. If the domain costs more than the credit, you pay the difference. The credit has no cash value and cannot be transferred.',
          'If the charge for the plan that gave you the credit is refunded or disputed, we may lock the transfer of the domain that was registered with the credit, because the credit was never paid for. See the [Billing and Refund Policy](doc:billing).',
        ],
      },
      {
        title: 'Connecting a domain you already own',
        blocks: [
          'You can connect a domain you registered elsewhere to a Site. You must own it or have permission to use it. You manage its DNS records where the domain is registered, and you must set the records we show you and keep them in place. DNS changes can take time to spread and are outside our control. We issue and renew the security certificate for the connected domain automatically. If you remove the records or the domain expires, your Site will stop being reachable at that address. Deleting a project does not change the DNS records; you must update them yourself.',
        ],
      },
      {
        title: 'Acceptable use of domains',
        blocks: [
          'You may not use a domain for anything that violates the [Acceptable Use Policy](doc:aup) or the rules of the registry. We may suspend, disconnect or lock a domain, or remove it from a Site, if it is used for phishing, malware, spam, infringement or other abuse, if a registry or authority requires it, or in a trademark or ownership dispute. Trademark disputes over a domain are handled under the policies of ICANN and the registry.',
        ],
      },
      {
        title: 'Responsibility',
        blocks: [
          'We are not liable for outages or errors of registries, registrars, DNS or certificate services, for DNS propagation delays, or for the loss of a domain because of an expired registration, a dispute or an action of a registry. The disclaimers and limits of liability in the Terms of Service apply.',
        ],
      },
    ],
  },

  es: {
    title: 'Términos de dominios',
    summary: 'Las reglas para registrar, renovar, transferir y conectar dominios.',
    intro: [
      'Estos Términos de dominios forman parte de los [Términos de servicio](doc:terms). Aplican si registras un dominio a través de véltica o conectas un dominio a un Sitio.',
    ],
    sections: [
      {
        title: 'Dominios que registras a través de véltica',
        subs: [
          {
            title: 'Nuestro papel',
            blocks: [
              'Revendemos el registro de dominios a través de un registrador socio. El registro y el registrador, no véltica, operan el sistema de dominios. El registro está sujeto a sus reglas y a las políticas de ICANN, incluida la Política uniforme de resolución de disputas de nombres de dominio. No podemos garantizar que un nombre que buscas esté disponible hasta que se complete el registro.',
            ],
          },
          {
            title: 'Tú eres el titular',
            blocks: [
              'El dominio se registra a tu nombre y tú eres el titular. Debes dar información de contacto exacta y actual y mantenerla al día, y debes responder a los correos de verificación del registrador o del registro. Si no lo haces, el dominio puede suspenderse. Los datos de contacto del titular se comparten con el registrador y el registro y pueden aparecer en directorios públicos de registro, salvo que se oculten o protejan según sus políticas.',
            ],
          },
          {
            title: 'Precio y pago',
            blocks: [
              'Los precios de los dominios dependen de la extensión e incluyen el coste que se nos cobra más una tarifa de servicio. El precio del registro y el de la renovación se muestran antes de comprar, y pueden ser distintos. Un dominio se cobra aparte de tu plan, salvo que tu plan incluya un crédito como se describe en el apartado 2.',
            ],
          },
          {
            title: 'Renovación y vencimiento',
            blocks: [
              'Cada dominio se renueva cada año en el aniversario de su registro, cobrando el método de pago que guardaste. Te avisamos antes de cada renovación y puedes apagar la renovación automática de un dominio. Si no se puede cobrar una renovación, el dominio puede vencer, y puede pasar a un periodo de redención con tarifas extra o liberarse para que cualquiera lo registre. No respondemos de un dominio que dejes vencer.',
            ],
          },
          {
            title: 'Sin reembolso una vez registrado o renovado',
            blocks: [
              'Una vez registrado o renovado un dominio, el cargo no se reembolsa, porque el registro no nos lo devuelve, salvo que la ley lo exija o que el registro fallara por un fallo nuestro.',
            ],
          },
          {
            title: 'Transferencias',
            blocks: [
              'El dominio es tuyo y puedes transferirlo a otro registrador. Puedes pedir el código de autorización de transferencia desde tu panel. Las transferencias están sujetas a las reglas del registro, incluido un bloqueo de 60 días tras un registro nuevo o un cambio de titular, y a los bloqueos descritos en estos términos.',
            ],
          },
        ],
      },
      {
        title: 'Dominio incluido con un plan anual',
        blocks: [
          'Algunos planes anuales incluyen un crédito para el primer año de registro de un dominio. El crédito aplica solo al registro, no a las renovaciones ni a otros cargos. Si el dominio cuesta más que el crédito, pagas la diferencia. El crédito no tiene valor en efectivo y no se puede transferir.',
          'Si se reembolsa o se disputa el cargo del plan que te dio el crédito, podemos bloquear la transferencia del dominio que se registró con ese crédito, porque el crédito nunca se pagó. Consulta la [Política de facturación y reembolsos](doc:billing).',
        ],
      },
      {
        title: 'Conectar un dominio que ya tienes',
        blocks: [
          'Puedes conectar a un Sitio un dominio que registraste en otro lugar. Debes ser titular o tener permiso para usarlo. Administras sus registros DNS donde el dominio está registrado, y debes poner los registros que te indicamos y mantenerlos. Los cambios de DNS pueden tardar en propagarse y escapan a nuestro control. Emitimos y renovamos automáticamente el certificado de seguridad del dominio conectado. Si quitas los registros o el dominio vence, tu Sitio dejará de estar accesible en esa dirección. Eliminar un proyecto no cambia los registros DNS; debes actualizarlos tú.',
        ],
      },
      {
        title: 'Uso aceptable de los dominios',
        blocks: [
          'No puedes usar un dominio para nada que incumpla la [Política de uso aceptable](doc:aup) o las reglas del registro. Podemos suspender, desconectar o bloquear un dominio, o quitarlo de un Sitio, si se usa para phishing, malware, spam, infracciones u otros abusos, si un registro o una autoridad lo exige, o en una disputa de marcas o de titularidad. Las disputas de marcas sobre un dominio se resuelven conforme a las políticas de ICANN y del registro.',
        ],
      },
      {
        title: 'Responsabilidad',
        blocks: [
          'No respondemos de caídas o errores de los registros, registradores, del DNS o de los servicios de certificados, de retrasos en la propagación del DNS, ni de la pérdida de un dominio por un registro vencido, una disputa o una acción de un registro. Aplican la exclusión de garantías y los límites de responsabilidad de los Términos de servicio.',
        ],
      },
    ],
  },
}
